import {
  GenerateTtsPreviewDocument,
  type GenerateTtsPreviewQuery,
  type GenerateTtsPreviewQueryVariables,
  GetMediaDocument,
  LinkMediaDocument,
  type LinkMediaMutation,
  type LinkMediaMutationVariables,
  MediaType,
  TextToSpeechDocument,
  type TextToSpeechMutation,
  type TextToSpeechMutationVariables,
  TtsProvider,
} from "@/generated/graphql";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MediaLinksPanel, TagsSection } from "../forms";
import { MediaPreview } from "../previews";
import { ModalShell } from "./ModalShell";

interface TextToSpeechModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFolderId?: string | null;
  mediaType: MediaType;
}

export function TextToSpeechModal({
  isOpen,
  onClose,
  currentFolderId,
  mediaType,
}: TextToSpeechModalProps) {
  // Input form state
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [provider, setProvider] = useState<TtsProvider>(TtsProvider.Azure);
  const [voice, setVoice] = useState("en-US-AndrewMultilingualNeural");

  // Preview state
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  // Metadata state
  const [_audioText, setAudioText] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [selectedLinks, setSelectedLinks] = useState<Partial<Record<MediaType, string[]>>>({});

  // Loading states
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Preview query
  const [generatePreview] = useLazyQuery<GenerateTtsPreviewQuery, GenerateTtsPreviewQueryVariables>(
    GenerateTtsPreviewDocument,
    {
      fetchPolicy: "network-only",
    }
  );

  // Save mutation
  const [textToSpeech, { loading: isSaving }] = useMutation<
    TextToSpeechMutation,
    TextToSpeechMutationVariables
  >(TextToSpeechDocument, {
    refetchQueries: [
      {
        query: GetMediaDocument,
        variables: { folderId: currentFolderId || undefined, type: mediaType },
      },
    ],
  });

  // Link media mutation
  const [linkMedia] = useMutation<LinkMediaMutation, LinkMediaMutationVariables>(LinkMediaDocument);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setText("");
        setFilename("");
        setProvider(TtsProvider.Azure);
        setVoice("en-US-AndrewMultilingualNeural");
        setAudioUrl(null);
        setAudioBlob(null);
        setAudioDuration(null);
        setAudioText("");
        setTags([]);
        setSelectedLinks({});
        setGenerationError(null);
      }, 300);
    }
  }, [isOpen]);

  // Clean up blob URL when component unmounts or audio changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const handleClose = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    onClose();
  };

  // Handle link changes
  const handleLinksChange = (type: MediaType, ids: string[]) => {
    setSelectedLinks((prev) => ({
      ...prev,
      [type]: ids,
    }));
  };

  // Generate audio preview
  const handleGenerateAudio = async (e: FormEvent) => {
    e.preventDefault();

    if (!text.trim()) {
      toast.error("Please enter text to convert");
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);

    try {
      const { data, error } = await generatePreview({
        variables: {
          text,
          voice,
          provider,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      if (!data?.generateTTSPreview.success) {
        throw new Error(data?.generateTTSPreview.error || "TTS generation failed");
      }

      if (!data.generateTTSPreview.audioBase64) {
        throw new Error("No audio data returned");
      }

      // Convert base64 to blob
      const binaryString = atob(data.generateTTSPreview.audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);

      // Clean up old URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      setAudioBlob(blob);
      setAudioUrl(url);
      setAudioDuration(data.generateTTSPreview.duration || null);
      setAudioText(text); // Pre-fill audioText with the original text
      toast.success("Audio preview generated!");
    } catch (error) {
      setGenerationError(error instanceof Error ? error.message : "Failed to generate audio");
      toast.error(error instanceof Error ? error.message : "Failed to generate audio");
    } finally {
      setIsGenerating(false);
    }
  };

  // Save to library
  const handleSave = async () => {
    if (!audioBlob) {
      toast.error("Please generate audio preview first");
      return;
    }

    if (!filename.trim()) {
      toast.error("Please enter a filename");
      return;
    }

    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }

    try {
      const result = await textToSpeech({
        variables: {
          input: {
            text,
            filename,
            folderId: currentFolderId || undefined,
            tags,
            voice,
            provider,
          },
        },
      });

      const savedAudioId = result.data?.textToSpeech.id;

      // Create media links if any
      if (savedAudioId) {
        for (const [, linkIds] of Object.entries(selectedLinks)) {
          for (const linkId of linkIds || []) {
            try {
              await linkMedia({
                variables: {
                  input: {
                    fromId: savedAudioId,
                    toId: linkId,
                  },
                },
              });
            } catch (linkError) {
              // Continue even if linking fails (might be duplicate)
              console.warn("Link creation failed:", linkError);
            }
          }
        }
      }

      toast.success(`Audio generated successfully: ${result.data?.textToSpeech.filename}`);
      handleClose();
    } catch (error) {
      toast.error(
        `Failed to save audio: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    }
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={isSaving || isGenerating ? () => {} : handleClose}
      title="Generate Audio from Text"
      singleColumn={true}
      leftPanel={
        <div className="space-y-6">
          {/* Filename Input */}
          <section>
            <label htmlFor="filename" className="mb-2 block text-sm font-medium text-gray-700">
              Filename <span className="text-red-500">*</span>
            </label>
            <input
              id="filename"
              type="text"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="my-audio-file"
              required
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">
              The file will be saved as {filename || "filename"}.mp3
            </p>
          </section>
          {/* Audio Preview (shows when generated) */}
          {audioUrl && (
            <section>
              <label className="mb-2 block text-sm font-medium text-gray-700">Audio preview</label>
              <div className="rounded-md border border-gray-200 p-3">
                <MediaPreview
                  url={audioUrl}
                  filename={`${filename || "preview"}.mp3`}
                  type={MediaType.Audio}
                  duration={audioDuration || undefined}
                  variant="inline"
                />
              </div>
            </section>
          )}

          {/* Text Input */}
          <section>
            <label htmlFor="text" className="mb-2 block text-sm font-medium text-gray-700">
              Audio Text <span className="text-red-500">*</span>
            </label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setAudioText(e.target.value);
              }}
              placeholder="Enter text to convert to speech..."
              rows={6}
              maxLength={5000}
              required
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">{text.length}/5000 characters</p>
          </section>

          {/* Provider Selector */}
          <section>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              TTS Provider <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="provider"
                  value={TtsProvider.Azure}
                  checked={provider === TtsProvider.Azure}
                  onChange={() => {
                    setProvider(TtsProvider.Azure);
                    setVoice("en-US-AndrewMultilingualNeural"); // Reset to Azure default
                  }}
                  disabled={isGenerating || isSaving}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Azure (Multilingual HD, SSML)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="provider"
                  value={TtsProvider.Openai}
                  checked={provider === TtsProvider.Openai}
                  onChange={() => {
                    setProvider(TtsProvider.Openai);
                    setVoice("alloy"); // Reset to OpenAI default
                  }}
                  disabled={isGenerating || isSaving}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">OpenAI (Better Context)</span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {provider === TtsProvider.Azure
                ? "Azure: Hundreds of voices with SSML control"
                : "OpenAI: Better at homographs and contextual pronunciation"}
            </p>
          </section>

          {/* Voice Selector */}
          <section>
            <label htmlFor="voice" className="mb-2 block text-sm font-medium text-gray-700">
              Voice
            </label>
            <select
              id="voice"
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
            >
              {provider === TtsProvider.Azure ? (
                <>
                  <option value="en-US-AndrewMultilingualNeural">
                    English (US) - Andrew (Male)
                  </option>
                  <option value="en-US-AvaMultilingualNeural">English (US) - Ava (Female)</option>
                  <option value="en-US-EmmaMultilingualNeural">English (US) - Emma (Female)</option>
                  <option value="en-US-DustinMultilingualNeural">
                    English (US) - Dustin (Male)
                  </option>
                  <option value="en-US-CoraMultilingualNeural">English (US) - Cora (Female)</option>
                  <option value="en-US-JennyMultilingualNeural">
                    English (US) - Jenny (Female)
                  </option>
                  <option value="en-US-AdamMultilingualNeural">English (US) - Adam (Male)</option>
                  <option value="en-GB-AdaMultilingualNeural">English (UK) - Ada (Female)</option>
                  <option value="en-GB-OllieMultilingualNeural">English (UK) - Ollie (Male)</option>
                </>
              ) : (
                <>
                  <option value="alloy">Alloy (Neutral)</option>
                  <option value="ash">Ash (Male)</option>
                  <option value="ballad">Ballad (Female)</option>
                  <option value="coral">Coral (Female)</option>
                  <option value="echo">Echo (Male)</option>
                  <option value="fable">Fable (British Male)</option>
                  <option value="onyx">Onyx (Deep Male)</option>
                  <option value="nova">Nova (Female)</option>
                  <option value="sage">Sage (Neutral)</option>
                  <option value="shimmer">Shimmer (Female)</option>
                  <option value="verse">Verse (Male)</option>
                </>
              )}
            </select>
          </section>

          {/* Generate Audio Button */}
          <button
            type="button"
            onClick={handleGenerateAudio}
            disabled={isGenerating || isSaving || !text.trim()}
            className="w-full rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  role="img"
                  aria-labelledby="loadingTitle"
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                >
                  <title id="loadingTitle">Generating audio...</title>
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Generating...
              </span>
            ) : (
              "Generate Audio Preview"
            )}
          </button>

          {/* Error message */}
          {generationError && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              <strong>Error:</strong> {generationError}
            </div>
          )}

          {/* Tags Section */}
          <TagsSection
            value={tags}
            onChange={setTags}
            required
            helpText="Enter tags for this audio file. Separate tags by commas."
          />

          {/* Media Links */}
          <MediaLinksPanel
            sourceMediaType={MediaType.Audio}
            sourceMediaId={null}
            selectedLinks={selectedLinks}
            onLinksChange={handleLinksChange}
          />
        </div>
      }
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isGenerating || isSaving}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              isGenerating || isSaving || !audioBlob || tags.length === 0 || !filename.trim()
            }
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save to Library"}
          </button>
        </>
      }
    />
  );
}
